/* ------------------------------------------------------------------
 * src/pages/edit-customers.jsx   (bug-fixed Version)
 * ------------------------------------------------------------------ */
import * as React from 'react';
import config from '../config/config';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';

import { Input } from './../components/form/Input';
import { MaskedTextBox } from './../components/form/MaskedTextBox';
import { DropDownList } from './../components/form/DropDownList';

import { requiredValidator } from './../validators';
import { AppContext } from '../AppContext';

const title = ['Herr', 'Frau', 'Dr.', 'Prof.'];

/* Hilfs-Funktion: beliebigen Firestore-Wert in echtes Boolean wandeln */
const toBool = (v) => v === true || v === 'true' || v === 'True';

const EditCustomers = () => {
  const { customerID } = useParams();
  const navigate = useNavigate();
  const { showNotification } = React.useContext(AppContext);

  const [initialValues, setInitialValues] = React.useState({
    isInPublicDirectory: false,
    isPrivateCustomer: false
  });
  const [loading, setLoading] = React.useState(true);
  const [isInPublicDirectory, setIsInPublicDirectory] = React.useState(false);
  const [isPrivateCustomer, setIsPrivateCustomer] = React.useState(false);

  /* ---------------- Daten laden ---------------- */
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.host}/customers/${customerID}`);
        if (!res.ok) throw new Error('not found');
        const data = await res.json();

        const publicDir = toBool(data['Active']);
        const privateCust = toBool(data['PrivateCustomer']);

        const values = {
          customerID   : data['Customer-ID'] || '',
          companyName  : data['Firma']       || '',
          streetName   : data['Strasse']     || '',
          streetNumber : data['Hausnummer']  || '',
          PLZ          : data['PLZ']         || '',
          cityName     : data['ORT']         || '',
          customer     : data['Titel']       || '',
          firstName    : data['Vorname']     || '',
          lastName     : data['Nachname']    || '',
          function     : data['Funktion']    || '',
          email        : data['EmailAdresse']|| '',
          phoneNumber  : data['Telefonnummer'] || '',
          isInPublicDirectory: publicDir,
          isPrivateCustomer  : privateCust
        };

        setInitialValues(values);
        setIsInPublicDirectory(publicDir);
        setIsPrivateCustomer(privateCust);
      } catch (err) {
        showNotification('error', 'Kunde nicht gefunden oder Fehler beim Laden.');
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    })();
  }, [customerID, navigate, showNotification]);

  /* ---------------- Submit --------------------- */
  const onSubmit = React.useCallback(async (dataItem) => {
    try {
      const res = await fetch(`${config.host}/customers/${customerID}`, {
        method : 'PUT',
        headers: { 'Content-Type':'application/json' },
        body   : JSON.stringify({
          Firma        : dataItem.companyName,
          Strasse      : dataItem.streetName,
          Hausnummer   : dataItem.streetNumber,
          PLZ          : dataItem.PLZ,
          ORT          : dataItem.cityName,
          Titel        : dataItem.customer,
          Vorname      : dataItem.firstName,
          Nachname     : dataItem.lastName,
          Funktion     : dataItem.function,
          Telefonnummer: dataItem.phoneNumber,
          EmailAdresse : dataItem.email,
          Active           : isInPublicDirectory,
          PrivateCustomer  : isPrivateCustomer
        })
      });

      if (!res.ok) throw new Error('update failed');
      showNotification('success', 'Kundendaten erfolgreich aktualisiert!');
      setInitialValues({ ...dataItem, isInPublicDirectory, isPrivateCustomer });
      navigate('/customers');
    } catch (err) {
      showNotification('error', 'Fehler beim Aktualisieren der Kundendaten.');
    }
  }, [customerID, navigate, isInPublicDirectory, isPrivateCustomer, showNotification]);

  /* ---------------- Hand­ler ------------------- */
  const handleSwitchChange = (e, fp) => {
    const v = e.value;
    setIsInPublicDirectory(v);
    fp?.onChange('isInPublicDirectory', { value: v });
  };

  const handlePrivateCustomerChange = (e, fp) => {
    const v = e.value;
    setIsPrivateCustomer(v);
    fp?.onChange('isPrivateCustomer', { value: v });
    fp?.onChange('companyName', { value: v ? 'PRIVATKUNDE' : '' });
  };

  const onCancelClick = () => navigate('/customers');

  /* ---------------- Render ---------------------- */
  if (loading) return <div>Loading…</div>;

  return (
    <div id="EditCustomers" className="profile-page main-content">
      <div className="card-container">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 className="card-title">Kundenverwaltung</h3>
        </div>

        <div className="card-component">
          <Form
            key={JSON.stringify(initialValues)}
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={(fp) => (
              <FormElement horizontal style={{ maxWidth:700 }}>
                <Field id="customerID"   name="customerID"   label="Kundennummer" component={Input} disabled />

                <Field id="companyName"  name="companyName"  label="Firmenname"  component={Input} validator={requiredValidator} />
                <Field id="streetName"   name="streetName"   label="Straße"       component={Input} validator={requiredValidator} />
                <Field id="streetNumber" name="streetNumber" label="Hausnummer"   component={Input} validator={requiredValidator} />
                <Field id="PLZ"          name="PLZ"          label="PLZ"          component={Input} validator={requiredValidator} />
                <Field id="cityName"     name="cityName"     label="Stadt"        component={Input} validator={requiredValidator} />

                <Field id="customer"   name="customer"   label="Anrede" component={DropDownList} data={title} />
                <Field id="firstName"  name="firstName"  label="Vorname" component={Input} />
                <Field id="lastName"   name="lastName"   label="Nachname" component={Input} validator={requiredValidator} />
                <Field id="function"   name="function"   label="Funktion" component={Input} />

                <Field id="email"        name="email"        label="E-Mail"        component={Input} />
                <Field id="phoneNumber"  name="phoneNumber"  label="Telefonnummer" component={MaskedTextBox} />

                <hr />
                <div style={{ marginTop:20, marginLeft:100 }}>
                  <label style={{ marginRight:10 }}>Kunde aktiv:</label>
                  <Switch checked={isInPublicDirectory} onChange={(e)=>handleSwitchChange(e, fp)} />
                </div>
                <div style={{ marginTop:20, marginLeft:100 }}>
                  <label style={{ marginRight:10 }}>Privatkunde:</label>
                  <Switch checked={isPrivateCustomer}   onChange={(e)=>handlePrivateCustomerChange(e, fp)} />
                </div>
                <hr />

                <div className="k-form-buttons">
                  <Button onClick={onCancelClick}>Abbrechen</Button>
                  <Button themeColor="primary" type="submit" disabled={!fp.allowSubmit}>Speichern</Button>
                </div>
              </FormElement>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default EditCustomers;
